import { useMutation as defaultUseMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';

import {
  Account,
  ApolloInjection,
  ExtendTrialMutation,
  UpgradeReason,
} from '@zapier/upsells/src/types';

import { canExtendTrial as _canExtendTrial } from './canExtendTrial';

type Props = {
  account: Account | undefined;
  reason: string;
  /**
   * If true, user will be automatically upgraded at the end of their trial extension.
   */
  shouldAutoUpgrade: boolean;
} & ApolloInjection;

export const extendTrialMutation = gql`
  mutation upsells_extendTrialMutation(
    $accountId: ID!
    $reason: String!
    $shouldAutoUpgrade: Boolean
  ) {
    extendAccountTrial(
      id: $accountId
      reason: $reason
      shouldAutoUpgrade: $shouldAutoUpgrade
    ) {
      id
    }
  }
`;

export const useExtendTrial = ({
  account,
  reason,
  shouldAutoUpgrade,
  useMutation,
}: Props) => {
  const _useMutation = useMutation || defaultUseMutation;
  const [
    extendTrial,
    { error: mutationError, loading: isLoadingMutation },
  ] = _useMutation<ExtendTrialMutation>(extendTrialMutation, {
    variables: {
      accountId: account?.id,
      reason,
      shouldAutoUpgrade,
    },
  });

  return {
    canExtendTrial: account
      ? _canExtendTrial({
          currentPlanAmount: account?.plan?.amount,
          enabledTrialExtensions: account?.enabledTrialExtensions,
          isTrialingCompanies: account?.isTrialingCompanies,
          isTrialingProfessional: account?.isTrialing,
          isTrialingTeams: account?.isTrialingTeams,
          reason: reason as UpgradeReason,
        })
      : false,
    error: mutationError,
    extendTrial,
    isLoading: isLoadingMutation,
  };
};
